import { FC, memo, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { TodoAppFrom, TodoTasksBoard } from '@demind-inc/core';

import './TaskProject.scss';
import { ProjectSelector } from '../../Project';
import { ProjectFilterItem } from '../../../hooks';
import { isAppleRemindersDesktopSupported } from '../../../helpers/appleRemindersDesktop.helpers';
import { useTranslation } from 'react-i18next';

interface TaskProjectProps {
  todoProjects: TodoTasksBoard[];
  isDisabled: boolean;
  selectedProject: TodoTasksBoard | null;
  onProjectSelect: (boardId: string) => void;
  onProjectAdd: () => void;
  currentAppFrom?: TodoAppFrom;
  hasRecurring?: boolean;
  hasCustomRecurrence?: boolean;
  enableToAdd?: boolean;
}

const TaskProject: FC<TaskProjectProps> = memo(
  ({
    todoProjects,
    selectedProject,
    onProjectSelect,
    onProjectAdd,
    isDisabled,
    currentAppFrom,
    hasRecurring,
    hasCustomRecurrence,
    enableToAdd = true,
  }) => {
    const { t } = useTranslation();
    // Transform TodoTasksBoard[] to ProjectFilterItem[] format
    const projectOptions = useMemo(() => {
      return todoProjects.map((project) => ({
        todoTaskId: project.todoTaskId,
        name: project.name,
        appFrom: project.appFrom,
      })) as ProjectFilterItem[];
    }, [todoProjects]);

    // Transform selectedProject to ProjectFilterItem format
    const selectedProjectItem = useMemo(() => {
      if (!selectedProject) return null;
      return {
        todoTaskId: selectedProject.todoTaskId,
        name: selectedProject.name,
        appFrom: selectedProject.appFrom,
      } as ProjectFilterItem;
    }, [selectedProject]);

    const handleProjectSelect = (projectId: string) => {
      onProjectSelect(projectId);
    };

    const isDisabledProject = useCallback(
      (project: ProjectFilterItem) => {
        if (project.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported())
          return true;
        if (hasRecurring && project.appFrom === 'clickup' && currentAppFrom !== 'clickup') {
          return true;
        }
        if (hasCustomRecurrence && currentAppFrom && project.appFrom !== currentAppFrom) {
          return true;
        }
        return false;
      },
      [hasRecurring, hasCustomRecurrence, currentAppFrom]
    );

    const getDisabledTooltip = useCallback(
      (project: ProjectFilterItem) => {
        if (project.appFrom === 'apple-reminders' && !isAppleRemindersDesktopSupported()) {
          return t('task.project.appleRemindersDesktopOnly');
        }
        if (hasRecurring && project.appFrom === 'clickup' && currentAppFrom !== 'clickup') {
          return t('task.project.recurringClickUpUnsupported');
        }
        if (hasCustomRecurrence && currentAppFrom && project.appFrom !== currentAppFrom) {
          return t('task.project.customRecurrenceMoveUnsupported');
        }
        return undefined;
      },
      [hasRecurring, hasCustomRecurrence, currentAppFrom, t]
    );

    return (
      <div className="task-project">
        <ProjectSelector
          projects={projectOptions}
          selectedProject={selectedProjectItem}
          onProjectSelect={handleProjectSelect}
          onProjectAdd={onProjectAdd}
          enableToAdd={enableToAdd}
          isDisabledProject={isDisabledProject}
          getDisabledProjectTooltip={getDisabledTooltip}
          disabled={isDisabled}
          className={clsx(
            'task-project__selector',
            isDisabled && 'task-project__selector--disabled',
            !selectedProject && 'task-project__selector--empty'
          )}
        />
      </div>
    );
  }
);

export default TaskProject;
